export const PHONE_PREFIXES = ["420", "421"];

export const DEFAULT_PHONE_PREFIX = "420";

function valueToString(value: any): string {
  return typeof value === "string"
    ? value
    : (value &&
        (value.target?.value ??
          value.value ??
          value.text ??
          value.toString?.())) ||
        "";
}

export function onlyDigits(value: any): string {
  return valueToString(value).replace(/\D/g, "");
}

export function onlyDigits9(value: any): string {
  return onlyDigits(value).slice(0, 9);
}

export function sanitizePhonePrefix(value: any): string {
  const prefix = onlyDigits(value).slice(0, 3);
  return PHONE_PREFIXES.includes(prefix) ? prefix : DEFAULT_PHONE_PREFIX;
}

export function phonePrefixOrDefault(value: any): string {
  return onlyDigits(value).slice(0, 3) || DEFAULT_PHONE_PREFIX;
}

export function splitPhoneNumber(prefix: any, phone: any) {
  const digits = onlyDigits(phone);

  if (digits.length > 9 && PHONE_PREFIXES.includes(digits.slice(0, 3))) {
    return {
      phoneNumberPrefix: digits.slice(0, 3),
      phoneNumber: digits.slice(-9),
    };
  }

  return {
    phoneNumberPrefix: sanitizePhonePrefix(prefix || DEFAULT_PHONE_PREFIX),
    phoneNumber: digits.slice(0, 9),
  };
}

export function formatPhone9(value: any): string {
  const digits = onlyDigits9(value || "");
  if (!digits) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export function normalizePhoneNumber(
  prefix?: any,
  phone?: any,
  defaultPrefix: any = DEFAULT_PHONE_PREFIX
): string | null {
  const raw = valueToString(phone);
  if (!raw.trim()) return null;

  const number = onlyDigits(raw);
  if (!number) return null;

  const trimmed = raw.replace(/^\s+/, "");

  if (trimmed.startsWith("+")) return `+${number}`;
  if (trimmed.startsWith("00") && number.length > 2) {
    return `+${number.slice(2)}`;
  }

  let phonePrefix = onlyDigits(prefix);
  if (!phonePrefix) phonePrefix = onlyDigits(defaultPrefix);

  if (!phonePrefix || number.startsWith(phonePrefix)) return `+${number}`;

  return `+${phonePrefix}${number}`;
}

export function formatPhoneNumber(value?: any): string {
  const raw = valueToString(value).trim();
  if (!raw) return "";

  const isInternational = raw.startsWith("+") || raw.startsWith("00");
  const digits = isInternational
    ? onlyDigits(raw).replace(/^00/, "")
    : onlyDigits(raw);

  if (!digits) return raw;

  if (!isInternational) {
    return digits.length === 9 ? formatPhone9(digits) : raw;
  }

  const knownPrefix = PHONE_PREFIXES.find((p) => digits.startsWith(p));
  if (knownPrefix && digits.length - knownPrefix.length <= 9) {
    const national = digits.slice(knownPrefix.length);
    return national ? `+${knownPrefix} ${formatPhone9(national)}` : raw;
  }

  if (digits.length > 9) {
    return `+${digits.slice(0, digits.length - 9)} ${formatPhone9(
      digits.slice(-9)
    )}`;
  }

  return raw;
}

export function formatPhone(prefix?: any, phone?: any): string {
  return formatPhoneNumber(normalizePhoneNumber(prefix, phone));
}
